import { useEffect, useState } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { useForm } from "react-hook-form";
import { get, post } from "../../utils/apiManager";
import { toast } from "react-toastify";
import { baseURL } from "../../config/config";

// A fee of 0 is a real choice (e.g. "$0 per sale"), so only an empty box means "no amount"
const hasAmount = (v) => v !== '' && v !== null && v !== undefined && !Number.isNaN(Number(v));

const inputS = (err) => ({ width: '100%', padding: '10px 14px', borderRadius: 8, border: `1.5px solid ${err ? '#AD2429' : '#e2e8f0'}`, fontSize: 14, color: '#0a1628', background: '#fff', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' });
const sectionTitle = (text) => (
  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#AD2429', marginBottom: 16, marginTop: 8 }}>{text}</div>
);

const LIFETIME_FEE_IDS = ['feeUpfrontFee', 'feeSalesPercentage', 'feeParSalesPercentage'];
const ALL_FEE_CHECKBOX_IDS = ['feeUpfrontFee', 'feeMonthlyFee', 'feeSalesPercentage', 'feeParSalesPercentage'];

const feeExclusiveWith = (id, period) => {
  const list = id === 'feeSalesPercentage'
    ? ['feeParSalesPercentage']
    : id === 'feeParSalesPercentage'
      ? ['feeSalesPercentage']
      : [];
  if (period === 'Yearly') {
    if (id === 'feeUpfrontFee') list.push('feeMonthlyFee');
    if (id === 'feeMonthlyFee') list.push('feeUpfrontFee');
  }
  return list;
};

const countFeesWithAmount = (data) => {
  let n = 0;
  if (data.feeUpfrontFee && data.upfrontFeeAmount) n += 1;
  if (data.feeMonthlyFee && data.monthlyFeeAmount) n += 1;
  if (data.feeSalesPercentage && data.salesPercentageAmount) n += 1;
  if (data.feeParSalesPercentage && data.parSalesPercentageAmount) n += 1;
  return n;
};

const validatePlanFees = (data) => {
  if (data.feeSalesPercentage && data.feeParSalesPercentage) {
    return 'Choose either Sales % fee or Flat fee per sale, not both.';
  }
  if (data.period === 'Lifetime') {
    const lifetimeFeeCount = LIFETIME_FEE_IDS.filter((id) => data[id]).length;
    if (lifetimeFeeCount !== 1) {
      return 'Lifetime plans: choose exactly one fee type — one-time price, sales % fee, or flat fee per sale.';
    }
    if (data.feeMonthlyFee) return 'Monthly fee cannot be used on a lifetime plan.';
    return null;
  }
  if (data.period === 'Monthly') {
    if (!data.feeMonthlyFee || !data.monthlyFeeAmount) {
      return 'Monthly plans must include Monthly subscription fee with a price.';
    }
    if (countFeesWithAmount(data) > 2) {
      return 'Select at most 2 fee types (e.g. monthly fee + sales %).';
    }
  }
  if (data.period === 'Yearly') {
    const hasAnnualSub = data.feeMonthlyFee && data.monthlyFeeAmount;
    const hasAnnualUpfront = data.feeUpfrontFee && data.upfrontFeeAmount;
    if (!hasAnnualSub && !hasAnnualUpfront) {
      return 'Yearly plans need Annual subscription fee or Annual upfront fee with a price.';
    }
    if (data.feeMonthlyFee && data.feeUpfrontFee) {
      return 'Yearly plans: choose Annual subscription fee OR Annual upfront fee, not both.';
    }
    if (countFeesWithAmount(data) > 2) {
      return 'Select at most 2 fee types (e.g. annual fee + sales %).';
    }
  }
  return null;
};

const feeLabelsForPeriod = (period) => {
  if (period === 'Yearly') {
    return {
      upfront: 'Annual upfront fee',
      upfrontAmount: 'Amount ($) per year',
      monthly: 'Annual subscription fee',
      monthlyAmount: 'Total price per year ($)',
      sales: 'Sales % fee',
      salesAmount: 'Percent (%) of hammer total',
      perSale: 'Flat fee per sale',
      perSaleAmount: 'Amount ($) per ended sale',
    };
  }
  if (period === 'Lifetime') {
    return {
      upfront: 'One-time price (lifetime)',
      upfrontAmount: 'Amount ($) — paid once',
      monthly: 'Monthly fee',
      monthlyAmount: 'Amount ($)',
      sales: 'Sales % fee',
      salesAmount: 'Percent (%) of hammer total',
      perSale: 'Flat fee per sale',
      perSaleAmount: 'Amount ($) per ended sale',
    };
  }
  return {
    upfront: 'Upfront fee',
    upfrontAmount: 'Amount ($)',
    monthly: 'Monthly subscription fee',
    monthlyAmount: 'Price per month ($)',
    sales: 'Sales % fee',
    salesAmount: 'Percent (%) of hammer total',
    perSale: 'Flat fee per sale',
    perSaleAmount: 'Amount ($) per ended sale',
  };
};

const FeeRow = ({ id, label, register, watch, setValue, amountField, amountLabel, errors, max, exclusive, maxTwoFees }) => {
  const checked = watch(id);
  const period = watch('period');
  const exclusiveWith = feeExclusiveWith(id, period);
  const { onChange, ...checkboxReg } = register(id);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '12px 0', borderBottom: '1px solid #f0f2f8' }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', minWidth: 160 }}>
        <input
          type="checkbox"
          {...checkboxReg}
          onChange={(e) => {
            if (e.target.checked) {
              if (maxTwoFees) {
                const otherChecked = ALL_FEE_CHECKBOX_IDS.filter((fid) => fid !== id && watch(fid)).length;
                if (otherChecked >= 2) {
                  toast.error('Select at most 2 fee types for this plan.');
                  return;
                }
              }
              if (exclusive) {
                LIFETIME_FEE_IDS.filter((fid) => fid !== id).forEach((fid) => setValue(fid, false));
              }
              exclusiveWith.forEach((fid) => setValue(fid, false));
            }
            onChange(e);
          }}
          style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#AD2429' }}
        />
        <span style={{ fontSize: 14, fontWeight: 600, color: '#0a1628' }}>{label}</span>
      </label>
      {checked && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <label style={{ fontSize: 13, color: '#6b7a99', whiteSpace: 'nowrap' }}>{amountLabel}</label>
          <div>
            <input placeholder={max ? "%" : "$"} type="number" step="0.01" min="0" max={max} style={{ ...inputS(errors[amountField]), width: 140 }}
              {...register(amountField, { required: checked, max: max ? { value: max, message: `Cannot exceed ${max}` } : undefined })} />
            {errors[amountField] && <span style={{ fontSize: 12, color: '#AD2429', display: 'block', marginTop: 2 }}>{errors[amountField].message || 'Required'}</span>}
          </div>
        </div>
      )}
    </div>
  );
};

const Edit = () => {
  const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm();
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const Navigate = useNavigate();
  const period = watch('period');
  const isLifetime = period === 'Lifetime';
  const maxTwoFees = period === 'Monthly' || period === 'Yearly';

  useEffect(() => {
    if (period === 'Lifetime') {
      setValue('feeMonthlyFee', false);
      setValue('monthlyFeeAmount', '');
    }
  }, [period, setValue]);

  useEffect(() => {
    const fetchData = async () => {
      const featuresList = await getAllFeatures();
      if (location?.search) {
        getOnePlans(location.search.split("?")[1], featuresList || []);
      }
    };
    fetchData();
  }, []);

  const getAllFeatures = async () => {
    try {
      const res = await get(`/plan-feature?page=1&perPage=1000&searchItem=`);
      const list = res?.data || res?.datas || [];
      setFeatured(list);
      return list;
    } catch (err) { console.log(err); return []; }
  };

  const getOnePlans = (id, featuresList) => {
    get(`/pricing/${id}`).then((res) => {
      if (res?.datas) {
        const d = res.datas;
        setValue("planName", d.planName);
        setValue("isActive", d.isActive);
        setValue("description", d.description);
        setValue("period", d.period);
        setValue("yearlyDiscountPercent", d.yearlyDiscountPercent || 20);
        setValue("isTrialAvailable", d.isTrialAvailable || false);
        setValue("trialDays", d.trialDays || 14);
        setValue("isPopular", d.isPopular || false);
        if (d.fees?.length > 0) {
          d.fees.forEach((fee) => {
            if (fee.type === 'upfrontFee') { setValue("feeUpfrontFee", true); setValue("upfrontFeeAmount", fee.amount); }
            if (fee.type === 'monthlyFee') { setValue("feeMonthlyFee", true); setValue("monthlyFeeAmount", fee.amount); }
            if (fee.type === 'salesPercentage') { setValue("feeSalesPercentage", true); setValue("salesPercentageAmount", fee.amount); }
            if (fee.type === 'parSalesPercentage') { setValue("feeParSalesPercentage", true); setValue("parSalesPercentageAmount", fee.amount); }
          });
        } else {
          if (d.upfrontFee === 'upfrontFee') { setValue("feeUpfrontFee", true); setValue("upfrontFeeAmount", d.amount); }
          if (d.upfrontFee === 'monthlyFee') { setValue("feeMonthlyFee", true); setValue("monthlyFeeAmount", d.amount); }
          if (d.upfrontFee === 'parSalesPercentage') { setValue("feeParSalesPercentage", true); setValue("parSalesPercentageAmount", d.amount); }
          if (d.upfrontFee === 'salesPercentage') { setValue("feeSalesPercentage", true); setValue("salesPercentageAmount", d.amount); }
        }
        if (d.featuresIds?.length > 0) {
          d.featuresIds.forEach((savedId) => {
            const index = featuresList.findIndex(f => f._id === savedId);
            if (index !== -1) setValue(`featuresIds[${index}].featureId`, savedId);
          });
        }
      }
    }).catch((err) => console.log(err));
  };

  const onSubmit = (data) => {
    let featuresIds = [];
    if (data?.featuresIds?.length > 0) data.featuresIds.forEach(item => { if (item.featureId) featuresIds.push(item.featureId); });
    let fees = [];
    if (data.feeUpfrontFee && hasAmount(data.upfrontFeeAmount)) fees.push({ type: 'upfrontFee', amount: Number(data.upfrontFeeAmount) });
    if (data.feeMonthlyFee && hasAmount(data.monthlyFeeAmount)) fees.push({ type: 'monthlyFee', amount: Number(data.monthlyFeeAmount) });
    if (data.feeSalesPercentage && hasAmount(data.salesPercentageAmount)) fees.push({ type: 'salesPercentage', amount: Number(data.salesPercentageAmount) });
    if (data.feeParSalesPercentage && hasAmount(data.parSalesPercentageAmount)) fees.push({ type: 'parSalesPercentage', amount: Number(data.parSalesPercentageAmount) });
    if (fees.length === 0) return toast.error("Please select at least one fee type");
    const feeError = validatePlanFees(data);
    if (feeError) return toast.error(feeError);
    setLoading(true);
    const payload = {
      featuresIds, description: data.description, period: data.period, planName: data.planName, fees,
      isActive: data.isActive, id: location.search.split("?")[1],
      isTrialAvailable: data.isTrialAvailable === true || data.isTrialAvailable === 'true',
      trialDays: parseInt(data.trialDays) || 14,
      isPopular: data.isPopular === true || data.isPopular === 'true',
    };
    post(`${baseURL}/pricing/edit`, payload).then((res) => { setLoading(false); if (res?.status) { toast.success(res.message || 'Plan updated'); Navigate("/admin/pricing"); } else toast.error(res?.message); })
      .catch((err) => { setLoading(false); toast.error(err?.response?.data?.error || 'Error updating plan'); });
  };

  return (
    <div style={{ fontFamily: "'Segoe UI', system-ui, sans-serif" }}>
      <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #edf0f7', boxShadow: '0 1px 4px rgba(26,39,68,.05)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 28px', borderBottom: '1px solid #edf0f7', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: '#0a1628', margin: 0 }}>Edit Plan</h3>
            <p style={{ fontSize: 13, color: '#9aa5c0', margin: '3px 0 0' }}>Update subscription plan details</p>
          </div>
          <button onClick={() => Navigate('/admin/pricing')} style={{ background: '#f0f2f8', color: '#5a6a8a', border: 'none', borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>← Back</button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} style={{ padding: '28px' }}>

          {sectionTitle('Plan Details')}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'flex', gap: 4 }}>Plan Name <span style={{ color: '#AD2429' }}>*</span></label>
              <input placeholder="Plan Name" type="text" style={inputS(errors.planName)} {...register("planName", { required: true })} />
              {errors.planName && <span style={{ fontSize: 12, color: '#AD2429', marginTop: 4, display: 'block' }}>Required</span>}
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'flex', gap: 4 }}>Period <span style={{ color: '#AD2429' }}>*</span></label>
              <select style={{ ...inputS(errors.period), appearance: 'none' }} {...register("period", { required: true })}>
                <option value="" disabled>Select period</option>
                <option value="Monthly">Monthly (auto-renew until cancelled)</option>
                <option value="Yearly">Yearly (auto-renew until cancelled)</option>
                <option value="Lifetime">Lifetime (one-time payment)</option>
              </select>
              {errors.period && <span style={{ fontSize: 12, color: '#AD2429', marginTop: 4, display: 'block' }}>Required</span>}
            </div>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'flex', gap: 4 }}>Plan Status <span style={{ color: '#AD2429' }}>*</span></label>
              <select style={{ ...inputS(errors.isActive), appearance: 'none' }} {...register("isActive", { required: true })}>
                <option value="" disabled>Select status</option>
                <option value={true}>Active</option>
                <option value={false}>Inactive</option>
              </select>
              {errors.isActive && <span style={{ fontSize: 12, color: '#AD2429', marginTop: 4, display: 'block' }}>Required</span>}
            </div>
            <div style={{ gridColumn: '1 / -1' }}>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'flex', gap: 4 }}>Description <span style={{ color: '#AD2429' }}>*</span></label>
              <textarea placeholder="Plan description..." rows={2} style={{ ...inputS(errors.description), resize: 'vertical' }} {...register("description", { required: true })} />
              {errors.description && <span style={{ fontSize: 12, color: '#AD2429', marginTop: 4, display: 'block' }}>Required</span>}
            </div>
          </div>

          {sectionTitle('Plan price — fee types')}
          {isLifetime && (
            <p style={{ fontSize: 12, color: '#6b7a99', margin: '-8px 0 12px' }}>Lifetime: choose one fee type only.</p>
          )}
          {(period === 'Monthly' || period === 'Yearly') && (
            <p style={{ fontSize: 12, color: '#6b7a99', margin: '-8px 0 12px' }}>
              {period === 'Monthly'
                ? 'Monthly: require monthly subscription fee; optional second fee (upfront, sales % fee or flat fee per sale).'
                : 'Yearly: pick Annual subscription fee OR Annual upfront (not both), plus an optional sales % fee or flat fee per sale.'}
            </p>
          )}
          <p style={{ fontSize: 12, color: '#6b7a99', margin: '-4px 0 12px' }}>
            Sale fees: the Sales % fee is a percent of each sale's hammer total (lots that sold, reserve met; no premium or tax).
            The flat fee is charged for every sale that ends. Both are billed on the 1st of each month for the sales that ended the
            month before, and are not charged for sales that end during a free trial.
          </p>
          <div style={{ background: '#f8f9fc', borderRadius: 10, padding: '4px 16px', marginBottom: 24, border: '1px solid #edf0f7' }}>
            {(() => {
              const fl = feeLabelsForPeriod(period);
              return (
                <>
                  <FeeRow id="feeUpfrontFee" label={fl.upfront} amountField="upfrontFeeAmount" amountLabel={fl.upfrontAmount} register={register} watch={watch} setValue={setValue} errors={errors} exclusive={isLifetime} maxTwoFees={maxTwoFees} />
                  {!isLifetime && (
                    <FeeRow id="feeMonthlyFee" label={fl.monthly} amountField="monthlyFeeAmount" amountLabel={fl.monthlyAmount} register={register} watch={watch} setValue={setValue} errors={errors} maxTwoFees={maxTwoFees} />
                  )}
                  <FeeRow id="feeSalesPercentage" label={fl.sales} amountField="salesPercentageAmount" amountLabel={fl.salesAmount} register={register} watch={watch} setValue={setValue} errors={errors} max={100} exclusive={isLifetime} maxTwoFees={maxTwoFees} />
                  <FeeRow id="feeParSalesPercentage" label={fl.perSale} amountField="parSalesPercentageAmount" amountLabel={fl.perSaleAmount} register={register} watch={watch} setValue={setValue} errors={errors} exclusive={isLifetime} maxTwoFees={maxTwoFees} />
                </>
              );
            })()}
          </div>

          {sectionTitle('Features')}
          <a href="/admin/plan-features" target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, color: '#1d4ed8', fontWeight: 600, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4, marginBottom: 12, display: 'block' }}>⚙ Manage Features →</a>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 24 }}>
            {featured.map((item, index) => (
              <label key={item._id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 12px', borderRadius: 8, border: '1px solid #edf0f7', background: '#fafbfd', fontSize: 13, color: '#3a4a6a' }}>
                <input type="checkbox" value={item._id} style={{ accentColor: '#AD2429' }} {...register(`featuresIds[${index}].featureId`)} />
                {item?.name}
              </label>
            ))}
          </div>

          {sectionTitle('Trial & display')}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 20, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 13, fontWeight: 600, color: '#3a4a6a', marginBottom: 6, display: 'block' }}>Trial Days</label>
              <input placeholder="14" type="number" min="0" style={inputS(false)} {...register("trialDays")} />
              <span style={{ fontSize: 11, color: '#9aa5c0', marginTop: 3, display: 'block' }}>Free trial length in days</span>
            </div>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', paddingTop: 4 }}>
              <input type="checkbox" style={{ marginTop: 2, accentColor: '#AD2429' }} {...register("isTrialAvailable")} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0a1628' }}>Enable Free Trial</div>
                <div style={{ fontSize: 11, color: '#9aa5c0', marginTop: 2 }}>Allow customers to try before subscribing</div>
              </div>
            </label>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', paddingTop: 4 }}>
              <input type="checkbox" style={{ marginTop: 2, accentColor: '#AD2429' }} {...register("isPopular")} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#0a1628' }}>Mark as Popular</div>
                <div style={{ fontSize: 11, color: '#9aa5c0', marginTop: 2 }}>Highlight this plan as most popular</div>
              </div>
            </label>
          </div>

          <div style={{ padding: '12px 16px', background: '#fffbeb', borderRadius: 8, border: '1px solid #fef3c7', fontSize: 12, color: '#92400e', marginBottom: 24 }}>
            <strong>Note:</strong> Monthly and Yearly plans auto-renew until cancelled. Lifetime plans are a one-time payment with no recurring billing.
          </div>

          <div style={{ paddingTop: 16, borderTop: '1px solid #edf0f7', display: 'flex', gap: 12 }}>
            <button type="submit" disabled={loading} style={{ background: '#AD2429', color: '#fff', border: 'none', borderRadius: 8, padding: '11px 28px', fontSize: 15, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: 'inherit', opacity: loading ? 0.7 : 1 }}>
              {loading ? 'Saving...' : 'Update Plan'}
            </button>
            <button type="button" onClick={() => Navigate('/admin/pricing')} style={{ background: '#f0f2f8', color: '#5a6a8a', border: 'none', borderRadius: 8, padding: '11px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Edit;
